import { VuexModule, Module, Mutation, Action } from 'vuex-module-decorators'
import { getNotifications } from '@/components/admin/users/users.service'
import SignalRServiceClass from '@/SignalRService'
import { DoctorInsurance, NotificationMessage } from '@/models'

const PATIENT_UPLOAD_KEY = 'patientUploadTracker'

function savePatientUploadTracker(state: {
  patientUploadId: string
  patientUploadTotal: number
  patientUploadLoaded: number
  patientUploadRemaining: number
  patientUploadPhase: string
  patientUploadError: string
}) {
  if (!state.patientUploadId) return
  sessionStorage.setItem(
    PATIENT_UPLOAD_KEY,
    JSON.stringify({
      uploadId: state.patientUploadId,
      total: state.patientUploadTotal,
      loaded: state.patientUploadLoaded,
      remaining: state.patientUploadRemaining,
      phase: state.patientUploadPhase,
      error: state.patientUploadError
    })
  )
}
@Module({ namespaced: true })
class AppModule extends VuexModule {
  public isLoading = false
  public takesLong = false
  public closeModal = false
  public progress = 0
  public isUploading = false
  public patientUploadInProgress = false
  public patientUploadId = ''
  public patientUploadTotal = 0
  public patientUploadLoaded = 0
  public patientUploadRemaining = 0
  public patientUploadPhase = 'idle'
  public patientUploadError = ''
  public messages: Notification[] = []
  public messageCount = 0
  public toastMessage: Notification = null
  public signalRService: SignalRServiceClass | null = null
  public massiveNotificationShowed = false
  public isUploadCompleted = false
  public userInsurances: DoctorInsurance[] = null

  @Action
  initSignalR(userId: string) {
    this.context.commit('setSignalRService', userId)
  }
  @Action
  stopSignalR(userId: string) {
    this.context.commit('stopSignalRService', userId)
  }
  @Action
  public async sendSignalRMessage(payload: NotificationMessage) {
    if (this.signalRService) {
      // console.log(payload.userId, payload.message)
      await this.signalRService.sendMessage(payload)
    }
  }

  @Mutation
  async setSignalRService(userId: string) {
    if (this.signalRService) {
      return
    }
    this.signalRService = new SignalRServiceClass(userId)
    await this.signalRService.startConnection()
  }
  @Mutation
  stopSignalRService(userId: string) {
    this.signalRService.stopConnection()
    this.signalRService = null
  }

  @Mutation
  setToastMessage(message: Notification) {
    this.toastMessage = message
  }
  @Action
  addLoading(isLoading: boolean) {
    this.context.commit('setLoading', isLoading)
  }
  @Action
  addTakesLong(takesLong: boolean) {
    this.context.commit('setTakesLong', takesLong)
    //console.log(takesLong)
  }
  @Action
  addProgress(progress: number) {
    this.context.commit('setProgress', progress)
  }
  @Action
  addMessage(message: string) {
    //console.log(message)
    this.context.commit('setMessage', message)
  }
  @Action
  async fetchNotifications(userId: string) {
    await getNotifications(userId).then(c => {
      this.context.commit('setNotifications', c.data)
    })
  }

  @Mutation
  setNotifications(notifications: Notification[]) {
    this.messages = notifications
  }

  @Action
  massiveLoadNotifications(flag: boolean) {
    this.context.commit('setMassiveLoadNotifications', flag)
  }

  @Mutation
  setMassiveLoadNotifications(flag: boolean) {
    this.massiveNotificationShowed = flag
  }

  @Action
  setUploadCompletedFlag(flag: boolean) {
    this.context.commit('setUploadCompleted', flag)
  }

  @Mutation
  setUploadCompleted(flag: boolean) {
    this.isUploadCompleted = flag
  }

  @Mutation
  setLoading(isLoading: boolean) {
    this.isLoading = isLoading
  }
  @Mutation
  setTakesLong(takesLong: boolean) {
    this.takesLong = takesLong
  }
  @Mutation
  setMessage(message: Notification) {
    this.messages.push(message)
  }

  @Mutation
  setProgress(progress: number) {
    this.progress = progress
    if (this.progress > 0 && this.progress < 100) {
      this.isUploading = true
    } else {
      this.isUploading = false
    }
  }
  @Action
  addCloseModal() {
    this.context.commit('setCloseModal')
  }

  @Mutation
  setCloseModal() {
    this.closeModal = true
  }

  @Action
  addUserInsurance(insurance: DoctorInsurance[]) {
    this.context.commit('setUserInsurance', insurance)
  }

  @Mutation
  setUserInsurance(insurance: DoctorInsurance[]) {
    this.userInsurances = insurance
    const ids =
      insurance
        ?.map(c => c.insuranceID)
        .filter(id => id != null)
        .join(',') || ''
    localStorage.setItem('userInsurances', ids)
  }

  @Mutation
  setPatientUploadInProgress(flag: boolean) {
    this.patientUploadInProgress = flag
  }

  @Mutation
  beginPatientUpload() {
    this.patientUploadInProgress = true
    this.patientUploadId = ''
    this.patientUploadTotal = 0
    this.patientUploadLoaded = 0
    this.patientUploadRemaining = 0
    this.patientUploadPhase = 'uploading'
    this.patientUploadError = ''
    sessionStorage.removeItem(PATIENT_UPLOAD_KEY)
  }

  @Mutation
  trackPatientUpload(payload: { uploadId: string; total: number }) {
    this.patientUploadInProgress = true
    this.patientUploadId = payload.uploadId
    this.patientUploadTotal = payload.total || 0
    this.patientUploadLoaded = 0
    this.patientUploadRemaining = payload.total || 0
    this.patientUploadPhase = 'processing'
    this.patientUploadError = ''
    savePatientUploadTracker(this)
  }

  @Mutation
  setPatientUploadCounts(payload: {
    total: number
    loaded: number
    remaining: number
  }) {
    this.patientUploadTotal = payload.total
    this.patientUploadLoaded = payload.loaded
    this.patientUploadRemaining = payload.remaining
    savePatientUploadTracker(this)
  }

  @Mutation
  markPatientUploadDone() {
    this.patientUploadPhase = 'done'
    this.patientUploadInProgress = true
    savePatientUploadTracker(this)
  }

  @Mutation
  failPatientUpload(message: string) {
    this.patientUploadInProgress = true
    this.patientUploadPhase = 'error'
    this.patientUploadError = message
    savePatientUploadTracker(this)
  }

  @Mutation
  restorePatientUpload(payload: {
    uploadId: string
    total: number
    loaded: number
    remaining: number
    phase: string
    error: string
  }) {
    this.patientUploadInProgress = true
    this.patientUploadId = payload.uploadId
    this.patientUploadTotal = payload.total || 0
    this.patientUploadLoaded = payload.loaded || 0
    this.patientUploadRemaining = payload.remaining || 0
    this.patientUploadPhase = payload.phase || 'processing'
    this.patientUploadError = payload.error || ''
  }

  @Mutation
  finishPatientUpload() {
    this.patientUploadInProgress = false
    this.patientUploadId = ''
    this.patientUploadTotal = 0
    this.patientUploadLoaded = 0
    this.patientUploadRemaining = 0
    this.patientUploadPhase = 'idle'
    this.patientUploadError = ''
    sessionStorage.removeItem(PATIENT_UPLOAD_KEY)
  }

  @Mutation
  setDefaultStore() {
    this.userInsurances = null
    this.messages = []
    this.patientUploadInProgress = false
    this.patientUploadId = ''
    this.patientUploadTotal = 0
    this.patientUploadLoaded = 0
    this.patientUploadRemaining = 0
    this.patientUploadPhase = 'idle'
    this.patientUploadError = ''
    localStorage.removeItem('userInsurances')
  }

  @Action
  resetStore() {
    this.context.commit('setDefaultStore')
  }

  get _closeModal() {
    return this.closeModal
  }

  get _getUserInsurances() {
    return this.userInsurances
  }
  get _getUserInsurancesID() {
    return this.userInsurances?.map(c => c.insuranceID)
  }
  get _getUserInsurancesName() {
    return this.userInsurances?.map(c => c.insuranceName)
  }

  get _messages() {
    return this.messages
  }
  get _isLoading() {
    return this.isLoading
  }
  get _takesLong() {
    return this.takesLong
  }
  get _progress() {
    return this.progress
  }
  get _isUploadCompleted() {
    return this.isUploadCompleted
  }

  get _isUploading() {
    return this.isUploading
  }
  get _patientUploadInProgress() {
    return this.patientUploadInProgress
  }
  get _messageCount() {
    return this.messages.length
  }
  get getSignalRService() {
    return this.signalRService
  }

  getDefaultState() {
    return {
      isLoading: false,
      takesLong: false,
      closeModal: false,
      progress: 0,
      isUploading: false,
      patientUploadInProgress: false,
      patientUploadId: '',
      patientUploadTotal: 0,
      patientUploadLoaded: 0,
      patientUploadRemaining: 0,
      patientUploadPhase: 'idle',
      patientUploadError: '',
      messages: [] as Notification[],
      messageCount: 0,
      toastMessage: null as Notification | null,
      signalRService: null as SignalRServiceClass | null,
      massiveNotificationShowed: false,
      isUploadCompleted: false,
      userInsurances: null as DoctorInsurance[] | null
    }
  }
}
export default AppModule
